import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Chip,
  IconButton,
  Typography,
  Paper,
  TextField,
  Alert,
  Tooltip,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import type { Address, Customer } from '../models/Customer';
import customerService from '../services/customerService';
import banService, { toE164Morocco } from '../services/banService';

interface Props {
  open: boolean;
  customer: Customer | null;
  onClose: () => void;
  onSaved?: () => void;
}

const emptyAddress = (): Address => ({
  id: crypto.randomUUID(),
  city: '',
  street: '',
  latitude: 0,
  longitude: 0,
  isDefault: false,
});

const CustomerEditDialog: React.FC<Props> = ({ open, customer, onClose, onSaved }) => {
  // Infos de base
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [email, setEmail]         = useState('');
  const phone                     = customer?.phone || '';

  // Adresses
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [addrSaving, setAddrSaving] = useState(false);

  // States
  const [savingInfo, setSavingInfo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bannissement
  const [isBannedLocal, setIsBannedLocal] = useState<boolean>(!!customer?.isBanned);
  const e164 = useMemo(() => toE164Morocco(customer?.phone || ''), [customer?.phone]);
  const [banLoading, setBanLoading] = useState(false);

  useEffect(() => {
    if (!open || !customer) return;
    // init infos
    setFirstName(customer.firstName || '');
    setLastName(customer.lastName || '');
    setEmail(customer.email || '');
    setIsBannedLocal(!!customer.isBanned);
    // init addresses
    setAddresses(customer.addresses ? [...customer.addresses] : []);
    setError(null);
  }, [open, customer]);

  const copyToClipboard = (value: string) => {
    navigator.clipboard?.writeText(value).catch(() => {});
  };

  const ensureSingleDefault = (items: Address[]) => {
    // S'assurer qu'il n'y ait qu'une seule adresse par défaut
    const hasDefault = items.some(a => a.isDefault);
    if (!hasDefault && items.length > 0) {
      items[0].isDefault = true;
    } else if (hasDefault) {
      // si plusieurs cochées, ne garder que la première
      let found = false;
      items = items.map(a => {
        if (a.isDefault && !found) {
          found = true;
          return a;
        }
        return { ...a, isDefault: false };
      });
    }
    return items.map(a => ({ ...a, isDefault: !!a.isDefault }));
  };

  const handleToggleDefault = (id: string) => {
    setAddresses(prev => prev.map(a => ({ ...a, isDefault: a.id === id })));
  };

  const handleAddAddress = () => {
    setAddresses(prev => ensureSingleDefault([...prev, emptyAddress()]));
  };

  const handleRemoveAddress = (id: string) => {
    setAddresses(prev => ensureSingleDefault(prev.filter(a => a.id !== id)));
  };

  const handleSaveInfo = async () => {
    if (!customer) return;
    try {
      setSavingInfo(true);
      await customerService.updateCustomer(customer.id, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() || undefined,
        // phone non modifiable
      });
      onSaved?.();
    } catch (e: any) {
      setError(e?.message || 'Erreur lors de la mise à jour');
    } finally {
      setSavingInfo(false);
    }
  };

  const handleSaveAddresses = async () => {
    if (!customer) return;
    try {
      setAddrSaving(true);
      await customerService.updateAddresses(customer.id, ensureSingleDefault(addresses));
      onSaved?.();
    } catch (e: any) {
      setError(e?.message || 'Erreur lors de la mise à jour des adresses');
    } finally {
      setAddrSaving(false);
    }
  };

  const handleBanSwitch = async (checked: boolean) => {
    if (!e164) {
      setError('Numéro invalide pour basculer le bannissement');
      return;
    }
    try {
      setBanLoading(true);
      if (checked) {
        await banService.banPhone(e164, 'Banni via admin (switch)');
        setIsBannedLocal(true);
      } else {
        await banService.unbanPhone(e164);
        setIsBannedLocal(false);
      }
      onSaved?.();
    } catch (e) {
      setError('Erreur lors du changement de statut de bannissement');
      // re-sync UI avec valeur précédente si erreur
      setIsBannedLocal(prev => !prev);
    } finally {
      setBanLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Modifier le client</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* En-tête nom + switch bannissement */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Prénom"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="Nom"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
          />
          <Box sx={{ flexGrow: 1 }} />
          <FormControlLabel
            control={
              <Switch
                checked={isBannedLocal}
                onChange={(e) => handleBanSwitch(e.target.checked)}
                disabled={banLoading || !e164}
                color="error"
              />
            }
            label={isBannedLocal ? 'Banni' : 'Non banni'}
          />
        </Box>

        {/* Téléphone / Email */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              label="Téléphone (non modifiable)"
              value={phone}
              size="small"
              InputProps={{ readOnly: true }}
              sx={{ flex: 1 }}
            />
            {phone ? (
              <Tooltip title="Copier le téléphone">
                <IconButton size="small" onClick={() => copyToClipboard(phone)}>
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>

          <TextField
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            size="small"
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveInfo}
            disabled={savingInfo}
          >
            Enregistrer les infos
          </Button>
        </Box>

        {/* Adresses (éditables) */}
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Adresses</Typography>

          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddAddress}>
              Ajouter une adresse
            </Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveAddresses} disabled={addrSaving}>
              Enregistrer les adresses
            </Button>
          </Box>

          {addresses.length === 0 ? (
            <Alert severity="info">Aucune adresse. Cliquez sur “Ajouter une adresse”.</Alert>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {addresses.map((a) => (
                <Paper key={a.id} sx={{ p: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(6, 1fr)' }, gap: 1, alignItems: 'center' }}>
                  <TextField
                    label="Ville"
                    value={a.city}
                    onChange={(e) => {
                      const v = e.target.value; setAddresses(prev => prev.map(x => x.id === a.id ? { ...x, city: v } : x));
                    }}
                    size="small"
                  />
                  <TextField
                    label="Région"
                    value={a.region || ''}
                    onChange={(e) => {
                      const v = e.target.value; setAddresses(prev => prev.map(x => x.id === a.id ? { ...x, region: v } : x));
                    }}
                    size="small"
                  />
                  <TextField
                    label="Code Postal"
                    value={a.postalCode || ''}
                    onChange={(e) => {
                      const v = e.target.value; setAddresses(prev => prev.map(x => x.id === a.id ? { ...x, postalCode: v } : x));
                    }}
                    size="small"
                  />
                  <TextField
                    label="Rue"
                    value={a.street}
                    onChange={(e) => {
                      const v = e.target.value; setAddresses(prev => prev.map(x => x.id === a.id ? { ...x, street: v } : x));
                    }}
                    size="small"
                  />
                  <TextField
                    label="Latitude"
                    type="number"
                    value={a.latitude}
                    onChange={(e) => {
                      const v = Number(e.target.value); setAddresses(prev => prev.map(x => x.id === a.id ? { ...x, latitude: v } : x));
                    }}
                    size="small"
                  />
                  <TextField
                    label="Longitude"
                    type="number"
                    value={a.longitude}
                    onChange={(e) => {
                      const v = Number(e.target.value); setAddresses(prev => prev.map(x => x.id === a.id ? { ...x, longitude: v } : x));
                    }}
                    size="small"
                  />
                  <TextField
                    label="Détails"
                    value={a.details || ''}
                    onChange={(e) => {
                      const v = e.target.value; setAddresses(prev => prev.map(x => x.id === a.id ? { ...x, details: v } : x));
                    }}
                    size="small"
                    sx={{ gridColumn: '1 / -1' }}
                  />
                  <Box sx={{ gridColumn: '1 / -1', display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Chip
                      label={a.isDefault ? 'Par défaut' : 'Définir par défaut'}
                      color={a.isDefault ? 'success' : 'default'}
                      onClick={() => handleToggleDefault(a.id)}
                      variant={a.isDefault ? 'filled' : 'outlined'}
                      size="small"
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    <IconButton color="error" onClick={() => handleRemoveAddress(a.id)} aria-label="Supprimer">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fermer</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerEditDialog;
